/* WF-047 · « Votre espace marchand » — the merchant's sign-in door (/connexion; the connexion ruling 19/10, lib-pack-18).
   One field, one button: the merchant asks for a magic link (POST /app/auth/magic-link, always 202), and the screen says
   ONE sentence whatever the address — no sentence of the app says whether an address has an espace (01 §1.1). The link
   lands back here with #token=: lib/auth reads it once, drops it from the address bar, exchanges it for the session and
   the page wanted (?suite=, a lost session's page) opens. A dead link is said in the contract's words (authLines) with
   the field ready for a new one. No Google button on the merchant side in V1, no password anywhere (the ruling). */
import { useEffect, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Field, Input, Note } from '@/ui'
import { authLines, exchangeToken, readFragmentToken, requestMagicLink, suiteOf } from '@/lib/auth'
import { Door } from './Door'

/** The 202 sentence: the same for every address (D-82 ⟦confirmable⟧). */
const SENT = 'Si cette adresse a un espace, le lien est parti\u00a0: ouvrez-le depuis ce téléphone ou cet ordinateur.'

export default function Connexion() {
  const navigate = useNavigate()
  const { search, hash } = useLocation()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState<'exchange' | 'send' | null>(null)
  const [sent, setSent] = useState(false)
  const [fieldLine, setFieldLine] = useState<string | null>(null)   // under the field: invalid_email, rate_limited, no answer
  const [line, setLine] = useState<string | null>(null)             // the dead link, said above the field

  /* the link's token, exchanged once on arrival (01 §1.2) — also when the link is opened in the tab already on this door
     (a hash change, no reload); then the page the merchant wanted, or the root */
  useEffect(() => {
    const t = readFragmentToken()
    if (!t) return
    setBusy('exchange'); setLine(null)
    exchangeToken(t)
      .then(() => navigate(suiteOf(search), { replace: true }))
      .catch(e => { setLine(authLines(e)); setBusy(null) })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash])

  const submit = async (ev: FormEvent) => {
    ev.preventDefault()
    if (!email.trim() || busy) return
    setBusy('send'); setFieldLine(null); setLine(null)
    try { await requestMagicLink(email); setSent(true) }
    catch (e) { setFieldLine(authLines(e)) }
    finally { setBusy(null) }
  }

  return (
    <Door testid="connexion">
      <div>
        <h1 className="t-page" style={{ margin: 0 }}>Votre espace marchand</h1>
        <p className="t-body" style={{ margin: '6px 0 0', color: 'var(--bmr-text-muted)' }}>Retrouvez votre salon.</p>
      </div>
      {busy === 'exchange' && <p className="t-body" style={{ margin: 0 }} data-testid="connexion-ligne" aria-live="polite">Connexion en cours…</p>}
      {line && <Note tone="crit" data-testid="connexion-ligne">{line}</Note>}
      {sent ? (
        <div className="card" style={{ display: 'grid', gap: 12 }}>
          <p className="t-body" style={{ margin: 0 }} data-testid="connexion-ligne" aria-live="polite">{SENT}</p>
          <div><Button variant="quiet" onClick={() => { setSent(false); setFieldLine(null) }}>Demander un autre lien</Button></div>
        </div>
      ) : busy !== 'exchange' && (
        <form className="card" onSubmit={submit} noValidate style={{ display: 'grid', gap: 14 }}>
          <Field label="Votre adresse e-mail" error={fieldLine ?? undefined}>
            {id => <Input id={id} type="email" autoComplete="email" inputMode="email" spellCheck={false} autoFocus
              value={email} onChange={e => setEmail(e.target.value)} data-testid="connexion-email" />}
          </Field>
          <div><Button variant="primary" type="submit" disabled={!email.trim() || busy === 'send'} data-testid="connexion-envoyer">Préparer un lien de connexion</Button></div>
          <p className="f-help" style={{ margin: 0 }}>Un accès existant retrouve le même établissement et ses brouillons.</p>
        </form>
      )}
    </Door>
  )
}
